import { createColumnHelper, flexRender, type SortingState, useTable } from "@tanstack/react-table"
import { Badge } from "design-system/atoms/Badge"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { Card } from "design-system/molecules/Card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "design-system/molecules/Table"
import { cn } from "design-system/utils"
import { sortingFeatures, type SortingFeatures } from "lib/table"
import { WhatsappTemplatePreview } from "modules/campaigns/components/Sections/channels/whatsapp/WhatsappTemplatePreview"
import { useCampaignTemplateMetricsQuery } from "modules/campaigns/lib/campaignTemplateMetricsQuery"
import { Loading } from "modules/shared/components/Loading"
import { useLanguageLabel } from "modules/shared/hooks/useLanguageLabel"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { CampaignDetailResponseDataSchema } from "schema"
import { z } from "zod"

type Props = { campaign: z.infer<typeof CampaignDetailResponseDataSchema> }
type CampaignTemplate = NonNullable<Props["campaign"]["templates"]>[number]
type WhatsappCampaignTemplate = Extract<CampaignTemplate, { channel: "Whatsapp" }>
type TemplateRow = {
  template: WhatsappCampaignTemplate
  name: string
  language: string
  isDefaultLanguage: boolean
  status: string
  sent: number
  open: number
  engage: number
  openRate: number
  engageRate: number
}

const columnHelper = createColumnHelper<SortingFeatures, TemplateRow>()
const numberFormatter = new Intl.NumberFormat()
const percentFormatter = new Intl.NumberFormat(undefined, {
  style: "percent",
  maximumFractionDigits: 0,
})
const numericColumns = new Set(["sent", "open", "engage", "openRate", "engageRate"])

export function CampaignWhatsappTemplateInsights({ campaign }: Props) {
  const { t } = useTranslation()
  const languageLabel = useLanguageLabel()
  const { data: metrics, isPending: isMetricsPending } = useCampaignTemplateMetricsQuery(
    campaign.id,
  )
  const [sorting, setSorting] = useState<SortingState>([])
  const templates = useMemo(
    () =>
      (campaign.templates ?? []).filter(
        (item): item is WhatsappCampaignTemplate => item.channel === "Whatsapp",
      ),
    [campaign.templates],
  )
  const rows = useMemo(() => {
    const metricsById = new Map(metrics?.map((item) => [item.templateId, item]) ?? [])
    const defaultLanguage = campaign.defaultLanguage?.split(/[-_]/)[0]?.toLowerCase()

    return templates
      .map((template) => {
        const usage = metricsById.get(template.id)
        return {
          template,
          name: template.name,
          language: languageLabel(template.language),
          isDefaultLanguage:
            Boolean(defaultLanguage) &&
            template.language.split(/[-_]/)[0]?.toLowerCase() === defaultLanguage,
          status: template.active ? "active" : "inactive",
          sent: usage?.timesUsed ?? 0,
          open: usage?.timesOpened ?? 0,
          engage: usage?.timesReplied ?? 0,
          openRate: usage?.openRate ?? 0,
          engageRate: usage?.engageRate ?? 0,
        }
      })
      .sort(
        (first, second) =>
          first.language.localeCompare(second.language) ||
          Number(second.template.active) - Number(first.template.active) ||
          first.name.localeCompare(second.name),
      )
  }, [campaign.defaultLanguage, languageLabel, metrics, templates])
  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor("name", {
          header: t("campaigns.templates.whatsapp.table.name"),
          cell: ({ row }) => {
            const { template } = row.original
            return (
              <div className="flex min-w-0 items-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="min-w-0 truncate text-left font-medium underline decoration-dotted underline-offset-4 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {template.name}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="right"
                    align="start"
                    variant="card"
                    className="max-h-[70vh] w-96 max-w-[calc(100vw-2rem)] overflow-y-auto p-2 [&_img]:-outline-offset-1 [&_img]:outline [&_img]:outline-1 [&_img]:outline-black/10 dark:[&_img]:outline-white/10"
                  >
                    <WhatsappTemplatePreview
                      templateId={template.id}
                      body={template.body}
                      header={template.header ?? undefined}
                      footer={template.footer ?? undefined}
                      media={{ format: template.mediaFormat, url: template.mediaUrl }}
                      variables={template.whatsappTemplateVariables}
                      buttonTexts={template.whatsappTemplateButtons?.map((button) => button.text)}
                    />
                  </TooltipContent>
                </Tooltip>
              </div>
            )
          },
        }),
        columnHelper.display({
          id: "approvalStatus",
          header: () => (
            <span className="sr-only">
              {t("campaigns.templates.whatsapp.table.approvalStatus")}
            </span>
          ),
          enableSorting: false,
          cell: ({ row }) => {
            const { status } = row.original.template
            if (!status || status === "APPROVED") return null

            let variant: "negative" | "warning" | "cached" = "cached"
            if (status === "REJECTED") variant = "negative"
            if (status === "PENDING") variant = "warning"

            return (
              <Badge variant={variant}>{t(`campaigns.templates.whatsapp.status.${status}`)}</Badge>
            )
          },
        }),
        columnHelper.accessor("language", {
          header: t("campaigns.templates.whatsapp.table.language"),
          cell: ({ row, getValue }) => (
            <span>
              {getValue()}
              {row.original.isDefaultLanguage && (
                <span className="ml-1.5 text-xs text-muted-foreground">
                  {t("campaigns.templates.whatsapp.campaign.defaultLanguage")}
                </span>
              )}
            </span>
          ),
        }),
        columnHelper.accessor("status", {
          header: t("campaigns.templates.whatsapp.table.status"),
          cell: ({ row }) => (
            <Badge variant={row.original.template.active ? "success" : "cached"}>
              {t(`campaigns.templates.whatsapp.actions.${row.original.status}`)}
            </Badge>
          ),
        }),
        columnHelper.accessor("sent", {
          header: t("campaigns.templates.whatsapp.insights.sent"),
          cell: (info) => numberFormatter.format(info.getValue()),
          sortFn: "basic",
          sortDescFirst: true,
        }),
        columnHelper.accessor("open", {
          header: t("campaigns.templates.whatsapp.insights.open"),
          cell: (info) => numberFormatter.format(info.getValue()),
          sortFn: "basic",
          sortDescFirst: true,
        }),
        columnHelper.accessor("engage", {
          header: t("campaigns.templates.whatsapp.insights.engage"),
          cell: (info) => numberFormatter.format(info.getValue()),
          sortFn: "basic",
          sortDescFirst: true,
        }),
        columnHelper.accessor("openRate", {
          header: t("campaigns.templates.whatsapp.insights.openRate"),
          cell: (info) => percentFormatter.format(info.getValue()),
          sortFn: "basic",
          sortDescFirst: true,
        }),
        columnHelper.accessor("engageRate", {
          header: t("campaigns.templates.whatsapp.insights.engageRate"),
          cell: (info) => percentFormatter.format(info.getValue()),
          sortFn: "basic",
          sortDescFirst: true,
        }),
      ]),
    [t],
  )
  const table = useTable({
    features: sortingFeatures,
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
  })
  const sortedRows = table.getRowModel().rows
  const visibleRows = sorting.some(({ id }) => id === "language")
    ? sortedRows
    : [
        ...sortedRows.filter((row) => row.original.isDefaultLanguage),
        ...sortedRows.filter((row) => !row.original.isDefaultLanguage),
      ]

  return (
    <section className="flex w-full flex-col gap-4">
      <div>
        <h3 className="text-base font-semibold leading-6">
          {t("campaigns.templates.whatsapp.insights.title")}
        </h3>
        <p className="text-xs leading-5 text-muted-foreground">
          {t("campaigns.templates.whatsapp.insights.description")}
        </p>
      </div>
      {templates.length === 0 && (
        <p className="py-4 text-sm text-muted-foreground">
          {t("campaigns.templates.whatsapp.insights.empty")}
        </p>
      )}
      {templates.length > 0 && isMetricsPending && <Loading />}
      {templates.length > 0 && !isMetricsPending && (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((group) => (
                <TableRow key={group.id}>
                  {group.headers.map((header) => {
                    const sortDirection = header.column.getIsSorted()
                    let ariaSort: "ascending" | "descending" | "none" = "none"
                    if (sortDirection === "asc") ariaSort = "ascending"
                    if (sortDirection === "desc") ariaSort = "descending"

                    return (
                      <TableHead
                        key={header.id}
                        align={numericColumns.has(header.column.id) ? "right" : "left"}
                        className="text-xs"
                        aria-sort={header.column.getCanSort() ? ariaSort : undefined}
                        sortable={
                          header.column.getCanSort()
                            ? {
                                active: Boolean(sortDirection),
                                direction: sortDirection === "desc" ? "desc" : "asc",
                                onClick: () => header.column.toggleSorting(),
                              }
                            : undefined
                        }
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    )
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {visibleRows.map((row) => (
                <TableRow key={row.id} className="h-12">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      align={numericColumns.has(cell.column.id) ? "right" : "left"}
                      className={cn(
                        "h-12 py-0 tabular-nums",
                        cell.column.id === "name" && "max-w-72",
                        cell.column.id === "approvalStatus" && "w-28",
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </section>
  )
}
